import React, { useMemo, useState } from 'react';
import moment from 'moment';
// Mantine charts package uses individual chart components
// Install: npm i @mantine/charts recharts moment
import { LineChart } from '@mantine/charts';
import { Divider, Menu, Paper, Text } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconChevronDown, IconPlaneArrival, IconPlaneDeparture } from "@tabler/icons-react";


// Single-file React component. Uses only plain HTML elements for layout
// and a single Mantine LineChart component for the graph.

export default function NewDelayStatistics({ apiData, dateRange, setDateRange, setDateUpdated }) {

    const [opened, setOpened] = useState(false);
    
    const getTimeAndDate = (dateStr) => {
    // Step 1: Remove timezone
    console.log(dateStr);
    const date = dateStr.split(" ")[0].trim(); // "2025-08-04 19:00"

    // Step 2: Extract date and time
    const time = dateStr.split(" ")[1].slice(0, 5);
    console.log(time); // date = "2025-08-04", time = "19:00"

    // Step 3: Format date as "04 Aug 25"
    const dateObj = new Date(date);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = dateObj.toLocaleString("en-US", { month: "short" });
    const year = String(dateObj.getFullYear());
    const formattedDate = `${day} ${month} ${year}`; // "04 Aug 25"

    const [hourStr, minute] = time.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;
    hour = hour === 0 ? 12 : hour; // handle midnight and noon

    return `${day} ${month} ${year} ${hour}:${minute} ${ampm}`;

    // return { date: formattedDate, time: `${hour}:${minute} ${ampm}` };
  };

  const rawApiData = apiData 

  const hhmmssToMinutes = (str) => {
    if (!str) return 0;
    const sign = str.startsWith('-') ? -1 : 1;
    const cleaned = str.replace(/^[-+]/, '');
    const [h, m, s] = cleaned.split(':').map(Number);
    return sign * (h * 60 + m + (s || 0) / 60);
  };

  const processed = useMemo(() => {
    const labels = rawApiData.map((d) => {
      const isoLike = d.from.local.replace(' ', 'T');
      return moment.parseZone(isoLike).format('HH:mm');
    });

    const dep = rawApiData.map((d) => hhmmssToMinutes(d.departuresDelayInformation.medianDelay));
    const arr = rawApiData.map((d) => hhmmssToMinutes(d.arrivalsDelayInformation.medianDelay));

    const totals = {
      totalDepartures: rawApiData.reduce((s, x) => s + (x.departuresDelayInformation.numTotal || 0), 0),
      totalArrivals: rawApiData.reduce((s, x) => s + (x.arrivalsDelayInformation.numTotal || 0), 0),
      avgDepDelay: Math.round(dep.reduce((s, v) => s + v, 0) / dep.length),
      avgArrDelay: Math.round(arr.reduce((s, v) => s + v, 0) / arr.length),
    };

    // Build chart data for Mantine LineChart
    const chartData = labels.map((label, i) => ({
      time: label,
      departures: dep[i],
      arrivals: arr[i],
    }));

    return { labels, dep, arr, totals, chartData };
  }, [rawApiData]);
    
    const bucketSize = 120;

function aggregateData(rawApiData) {
  const buckets = {};

  rawApiData.forEach((d) => {
    const isoLike = d.from.local.replace(" ", "T");
    const time = moment.parseZone(isoLike);

    // Find the start of the bucket (e.g., 10:00 → 10:00, 11:15 → 10:00 for 2-hour bins)
    const minutes = time.hours() * 60 + time.minutes();
    const bucketStart = Math.floor(minutes / bucketSize) * bucketSize;

    const bucketLabel = moment(time)
      .startOf("day")
      .add(bucketStart, "minutes")
      .format("HH:mm");

    if (!buckets[bucketLabel]) {
      buckets[bucketLabel] = { dep: [], arr: [] };
    }

    buckets[bucketLabel].dep.push(
      hhmmssToMinutes(d.departuresDelayInformation.medianDelay)
    );
    buckets[bucketLabel].arr.push(
      hhmmssToMinutes(d.arrivalsDelayInformation.medianDelay)
    );
  });

  // Reduce each bucket to averages
  return Object.entries(buckets).map(([label, values]) => ({
    time: label,
    departures:
      values.dep.reduce((s, v) => s + v, 0) / values.dep.length || 0,
    arrivals: values.arr.reduce((s, v) => s + v, 0) / values.arr.length || 0,
  }));
    }
    

    const aggregatedChunkData = aggregateData(rawApiData);
function CustomLegend({ payload }) {
  if (!payload) return null;

  return (
    <div
      style={{
        display: "flex",
        gap: "16px",
        marginBottom: "12px",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {payload.map((entry, index) => (
        <div key={`legend-${index}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              backgroundColor: entry.color,
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 500 }}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

function CustomTooltip({ label, payload, active }) {
  if (!active || !payload || payload.length === 0) return null;

  const dep = payload.find((p) => p.dataKey === "departures");
  const arr = payload.find((p) => p.dataKey === "arrivals");

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.08)",
        padding: "8px 12px",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {dep && (
        <div>
          Departures: <strong>{Math.round(dep.value)} min</strong>
        </div>
      )}
      {arr && (
        <div >
          Arrivals: <strong>{Math.round(arr.value)} min</strong>
        </div>
      )}
    </div>
  );
}


  return (
    <div className='delay-container' style={{  maxWidth: 900, margin: '0 auto', fontFamily: 'Inter, sans-serif', color: '#111827' }}>
      <div className="delay-header-wrapper" >
             <div className="delay-header">
               <h6>
                 Airport:{" "}
                 {apiData[0]?.airportIcao ? apiData[0]?.airportIcao : ""}
               </h6>
               <span>
                 {getTimeAndDate(apiData[0].from.local)} → {getTimeAndDate(apiData.at(-1).to.local)}
               </span>
             </div>
             <Menu opened={opened} onChange={setOpened} position='bottom-end'>
               <Menu.Target>
                 <span className="select-date">
                   {dateRange[0] && dateRange[1]
                     ? `${moment(dateRange[0]).format("MM/DD/YYYY")} - ${moment(dateRange[1]).format("MM/DD/YYYY")}`
                     : "Select Date"}
     
                   <IconChevronDown size={"15px"} />
                 </span>
               </Menu.Target>
     
               <Menu.Dropdown>
                 <div
                   style={{
                     padding: "20px",
                     boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
                   }}
                 >
                   <DatePicker
                     value={dateRange}
                     onChange={setDateRange}
                     maxDate={new Date()}
                     type="range" allowSingleDateInRange
                   />
     
                   <div style={{ display: "flex", justifyContent: "space-between" }}>
                     <span></span>
     
                     <button
                       className="update-btn"
                       onClick={() => {
                         setDateUpdated(true);
                         setOpened(false);
                       }}
                     >
                       Update
                     </button>
                   </div>
                 </div>
               </Menu.Dropdown>
             </Menu>
          </div>
          
                <Divider my="md" />
          

      <div  className='delay-details-wrapper'>
        <div className='delay-details-box' >
          <h2 >Departures <IconPlaneDeparture size={"20px"} /></h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12 }}>
            <div className='delay-box-item'>
              <p >{processed.totals.totalDepartures}</p>
              <p >Total Flights</p>
            </div>
            <div className='delay-box-item'>
              <p >{processed.totals.avgDepDelay} min</p>
              <p >Avg. Delay</p>
            </div>
          </div>
        </div>

        <div className='delay-details-box'  >
          <h2 >Arrivals <IconPlaneArrival size={"20px"} /></h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12 }}>
            <div className='delay-box-item'>
              <p >{processed.totals.totalArrivals}</p>
              <p >Total Flights</p>
            </div>
            <div className='delay-box-item'>
              <p >{processed.totals.avgArrDelay} min</p>
              <p >Avg. Delay</p>
            </div>
          </div>
        </div>
      </div>

      <div className='graph-wrapper' style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb' }}>
        {/* <h2 style={{ marginTop: 0, fontSize: 18, fontWeight: 600, color: '#374151' }}>Median Delay Over Time ()</h2> */}
        <div >
          <LineChart
            h={340}
            data={aggregatedChunkData}
            dataKey="time"
            series={[
              { name: 'departures', color: 'blue.6' },
              { name: 'arrivals', color: 'red.6' },
                      ]}
                      
                      tooltipAnimationDuration={200}
            curveType="monotone"
            withLegend
  tooltipProps={{
    content: (props) => <CustomTooltip {...props} />,
                      }}
                       legendProps={{
    content: (props) => <CustomLegend {...props} />,
  }}
            yAxisLabel="Minutes"
          />
        </div>
      </div>
    </div>
  );
}


